/**
 * Template Extractor Utility
 * 
 * This utility extracts template data from existing scratch card configurations.
 * It captures all design aspects including components, spacing, padding, etc.
 */

import { 
  MEGA_MILLIONS_CONFIG, 
  QUICK_CASH_CONFIG, 
  CASH_KING_CONFIG 
} from '../Games/ScratchCardConfigs';

/**
 * Extract template data from an existing card configuration
 * @param {Object} config - The card configuration object
 * @param {Object} additionalProps - Additional properties to include in the template
 * @returns {Object} The extracted template object
 */
export function extractTemplateFromConfig(config, additionalProps = {}) {
  return {
    id: `template-${config.id}`,
    name: `${config.name} Template`,
    description: `Template based on the ${config.name} scratch card design`,
    thumbnail: `/assets/templates/${config.id.toLowerCase()}-preview.png`,
    baseConfig: {
      gameType: config.id,
      bgColor: config.bgColor,
      accentColor: config.accentColor,
      cost: config.cost,
      maxPrize: config.maxWin || 100000
    },
    layout: {
      gridSize: config.gridSize || { rows: 3, columns: 3 },
      cardRatio: "3:4",
      padding: { top: 16, right: 16, bottom: 16, left: 16 },
      spacing: 8
    },
    components: {
      header: {
        height: 80,
        fontSize: 24,
        fontWeight: "bold",
        alignment: "center",
        padding: { top: 16, right: 16, bottom: 16, left: 16 }
      },
      cells: {
        borderRadius: 8,
        borderWidth: 1,
        fontSize: 18,
        symbolSize: 32
      },
      winningSymbolsRow: {
        height: 60,
        symbolSize: 24,
        spacing: 8,
        alignment: "center"
      },
      footer: {
        height: 60,
        fontSize: 14,
        padding: { top: 8, right: 16, bottom: 8, left: 16 }
      }
    },
    gameLogic: {
      winningSymbols: config.winningSymbols || ['🎁', '⭐', '👑', '💎', '🔔'],
      winConditions: [
        { matchCount: 1, prizeMultiplier: 1 },
        { matchCount: 2, prizeMultiplier: 2 },
        { matchCount: 3, prizeMultiplier: 5 }
      ],
      defaultPrizeTiers: 3
    },
    animations: {
      reveal: {
        type: "scratch",
        duration: 500,
        easing: "ease-out"
      },
      win: {
        type: "confetti",
        duration: 2000,
        particles: true
      }
    },
    createdAt: new Date(),
    updatedAt: new Date(),
    author: "System",
    tags: [
      config.id.toLowerCase().replace('_', '-'), 
      getColorTag(config.bgColor),
      `grid-${config.gridSize?.rows || 3}x${config.gridSize?.columns || 3}`
    ],
    ...additionalProps
  };
}

/**
 * Get a color tag based on the background color
 * @param {string} bgColor - The background color
 * @returns {string} The color tag
 */
function getColorTag(bgColor) {
  if (typeof bgColor === 'string') {
    if (bgColor.includes('red') || bgColor.includes('#8B0000') || bgColor.includes('#DC143C')) {
      return 'dark-red';
    } else if (bgColor.includes('blue')) {
      return 'blue';
    } else if (bgColor.includes('green')) {
      return 'green';
    } else if (bgColor.includes('purple')) {
      return 'purple';
    } else if (bgColor.includes('gold') || bgColor.includes('yellow')) {
      return 'gold';
    }
  }
  return 'other';
}

/**
 * Generate default templates from existing configurations
 * @returns {Array} Array of template objects
 */
export function generateDefaultTemplates() {
  const templates = [
    extractTemplateFromConfig(MEGA_MILLIONS_CONFIG, {
      tags: ['premium', 'dark-red', 'grid-5x5']
    }),
    extractTemplateFromConfig(QUICK_CASH_CONFIG, {
      tags: ['budget', 'blue', 'grid-3x3']
    }),
    extractTemplateFromConfig(CASH_KING_CONFIG, {
      tags: ['standard', 'green', 'grid-4x4']
    })
  ];
  
  return templates;
}

/**
 * Apply a template to create a new game configuration
 * @param {Object} template - The template to apply
 * @param {Object} customizations - Custom overrides for the template
 * @returns {Object} The new game configuration
 */
export function applyTemplate(template, customizations = {}) {
  // Start with template as base
  const gameConfig = {
    // Base game properties
    id: `game-${Date.now()}`,
    title: customizations.title || template.name.replace(' Template', ''),
    description: customizations.description || template.description,
    
    // Apply template base config
    gameType: template.baseConfig.gameType,
    bgColor: customizations.bgColor || template.baseConfig.bgColor,
    accentColor: customizations.accentColor || template.baseConfig.accentColor,
    cost: customizations.cost || template.baseConfig.cost,
    maxPrize: customizations.maxPrize || template.baseConfig.maxPrize,
    
    // Apply template layout
    gridSize: customizations.gridSize || template.layout.gridSize,
    cardRatio: template.layout.cardRatio,
    padding: template.layout.padding,
    spacing: template.layout.spacing,
    
    // Apply component styling
    components: {
      ...template.components,
      // Override with any custom component styling
      ...(customizations.components || {})
    },
    
    // Apply game logic
    winningSymbols: customizations.winningSymbols || template.gameLogic.winningSymbols,
    winConditions: customizations.winConditions || template.gameLogic.winConditions,
    
    // Apply animations
    animations: template.animations,
    
    // Game state
    status: 'DRAFT',
    createdAt: new Date(),
    createdBy: customizations.createdBy || 'anonymous',
    
    // Template reference
    templateId: template.id
  };
  
  return gameConfig;
}
